import {
    Count,
    DishDaily,
    DishFlags,
    DishInfo,
    DishKind,
    DishTags,
    EateryAudience,
    EateryCard,
    EateryDecor,
    EateryDiscount,
    EateryDomain,
    EateryFlagsInfo,
    EateryGroup,
    EateryImpression,
    EateryInfo,
    EateryTagsInfo,
    EateryTagsStats,
    EntityFlags,
    FeedbackInfo,
    Flag2Stats,
    PriceRange,
    SectorInfo,
    Session,
    SessionInfo,
    Topic,
    TopicInfo,
    UserCard,
    UserCrime,
    UserInfo,
    UserIssueRequestTicket,
    UserPreferences,
} from 'api'

export const ENTITY_FLAGS_DEFAULT: EntityFlags = {
    is_alive: false,
}

export const SESSION_INFO_DEFAULT: SessionInfo = {
    client: 0,
    os: 0,
    browser: 0,
    device: 0,
    client_version: 0,
    os_version: 0,
    browser_version: 0,
}

export const SESSION_DEFAULT: Session = {
    ip: '',
    info: SESSION_INFO_DEFAULT,
    timestamp: 0,
    token: '',
}

export const USER_TICKET_DEFAULT: UserIssueRequestTicket = {
    issue: '',
    response_at: 0,
    status: 'none',
}

export const SECTOR_DEF: SectorInfo = {
    canton_id: '',
    canton_name: '',
    nation_id: '',
    nation_name: '',
    region_id: '',
    region_name: '',
}

export const FLAG_2_STATS_DEF: Flag2Stats = {
    no: 0,
    yes: 0,
}

export const EATERY_TAGS_STATS_DEF: EateryTagsStats = {
    allows_long_stays: FLAG_2_STATS_DEF,
    can_smoke_indoor: FLAG_2_STATS_DEF,
    has_breakfast: FLAG_2_STATS_DEF,
    has_delivery: FLAG_2_STATS_DEF,
    has_dine_in: FLAG_2_STATS_DEF,
    has_dinner: FLAG_2_STATS_DEF,
    has_dj: FLAG_2_STATS_DEF,
    has_drive_through: FLAG_2_STATS_DEF,
    has_good_view: FLAG_2_STATS_DEF,
    has_halal_food: FLAG_2_STATS_DEF,
    has_indoor_seating: FLAG_2_STATS_DEF,
    has_live_music: FLAG_2_STATS_DEF,
    has_lunch: FLAG_2_STATS_DEF,
    has_outdoor_seating: FLAG_2_STATS_DEF,
    has_parking: FLAG_2_STATS_DEF,
    has_private_dining: FLAG_2_STATS_DEF,
    has_qelyan: FLAG_2_STATS_DEF,
    has_reservations: FLAG_2_STATS_DEF,
    has_self_service: FLAG_2_STATS_DEF,
    has_solo_dining: FLAG_2_STATS_DEF,
    has_takeaway: FLAG_2_STATS_DEF,
    has_toilet: FLAG_2_STATS_DEF,
    has_tv: FLAG_2_STATS_DEF,
    has_vegan_food: FLAG_2_STATS_DEF,
    has_vegetarian_food: FLAG_2_STATS_DEF,
    has_wifi: FLAG_2_STATS_DEF,
    is_photogenic: FLAG_2_STATS_DEF,
    is_suitable_for_children: FLAG_2_STATS_DEF,
    is_suitable_for_large_groups: FLAG_2_STATS_DEF,
    is_suitable_for_pets: FLAG_2_STATS_DEF,
    is_suitable_for_reading: FLAG_2_STATS_DEF,
    is_suitable_for_social_working: FLAG_2_STATS_DEF,
    is_tourist_friendly: FLAG_2_STATS_DEF,
    is_wheelchair_accessible: FLAG_2_STATS_DEF,
}

export const IMPORTANT_EATERY_TAGS: (keyof EateryTagsStats)[] = [
    'allows_long_stays',
    'can_smoke_indoor',
    'has_dj',
    'has_good_view',
    'has_live_music',
    'has_indoor_seating',
    'has_outdoor_seating',
    'has_parking',
    'has_qelyan',
    'has_reservations',
    'has_toilet',
    'has_wifi',
    'is_photogenic',
    'is_suitable_for_children',
    'is_suitable_for_large_groups',
    'is_suitable_for_pets',
    'is_suitable_for_reading',
    'is_suitable_for_social_working',
    'is_wheelchair_accessible',
]

export const USER_PREFERENCES_DEF: UserPreferences = {
    entity_flags: ENTITY_FLAGS_DEFAULT,
    gene: '',
    growth: 0,
    user: '',
    eatery: {
        audience: {
            business: 0,
            couples: 0,
            family: 0,
            friends: 0,
            none: 0,
        },
        decor: {
            bookish: 0,
            cultural: 0,
            fantasy: 0,
            game: 0,
            luxury: 0,
            minimal: 0,
            modern: 0,
            musical: 0,
            nature: 0,
            none: 0,
            street: 0,
            traditional: 0,
        },
        domain: {
            ash: 0,
            bakery: 0,
            cafe: 0,
            dessert: 0,
            dizi: 0,
            falafel: 0,
            fast_food: 0,
            fried: 0,
            gelato: 0,
            hot_dog: 0,
            ice_cream: 0,
            juicery: 0,
            kababi: 0,
            kalle_pache: 0,
            liver: 0,
            none: 0,
            restaurant: 0,
            sandwich: 0,
            seafood: 0,
            teahouse: 0,
        },
        gender_specific: {
            female: 0,
            male: 0,
            none: 0,
        },
        group: {
            cafe: 0,
            fast_food: 0,
            dessert: 0,
            restaurant: 0,
            unknown: 0,
        },
        tags: EATERY_TAGS_STATS_DEF,

        // always null
        count: 0,
        oldest: null,
    },
}

export const USER_INFO_DEFAULT: UserInfo = {
    gene: '',
    agent: null,
    review: null,
    photo: '',
    created_at: 0,
    entity_flags: ENTITY_FLAGS_DEFAULT,
    name: '',
    sessions: [SESSION_DEFAULT, SESSION_DEFAULT, SESSION_DEFAULT],
    session_index: 0,
    flags: {
        has_password: false,
        is_banned: false,
        is_jailed: false,
        is_invited: false,
    },
    online_at: 0,
    birth_date: 0,
    gender: 'unknown',
    home_location: {
        latitude: 0,
        longitude: 0,
        altitude: 0,
        level: 0,
        sector: SECTOR_DEF,
    },
    username: '',
    wallet: {
        irr: 0,
        rob: 0,
    },
    telephone: {
        cc: 0,
        phone: '',
    },
    preferences: USER_PREFERENCES_DEF,
    transactions: null,
    notifications: false,
    compact_gene: '',
    history_eatery_nav: null,
    history_eatery_view: null,
    saved_dishes: null,
    saved_eateries: null,
    inventory: {},
}

export const CRIME_DEFAULT: UserCrime = {
    cause: 'unknown',
    severity: 1,
    timestamp: 0,
}

export const BEST_DISH_DEFAULT: EateryInfo['best_dish'] = [
    null,
    null,
    null,
    null,
    null,
]

export const EATERY_DOMAIN_GROUP = {
    none: 'unknown',

    cafe: 'cafe',
    bakery: 'cafe',
    teahouse: 'cafe',

    restaurant: 'restaurant',
    seafood: 'restaurant',
    dizi: 'restaurant',
    ash: 'restaurant',
    kababi: 'restaurant',
    kalle_pache: 'restaurant',
    liver: 'restaurant',

    fast_food: 'fast_food',
    hot_dog: 'fast_food',
    sandwich: 'fast_food',
    falafel: 'fast_food',
    fried: 'fast_food',

    dessert: 'dessert',
    ice_cream: 'dessert',
    juicery: 'dessert',
    gelato: 'dessert',
} as const satisfies Record<EateryDomain, EateryGroup>

export const EATERY_GROUP_DOMAINS: Record<EateryGroup, EateryDomain[]> = {
    unknown: [],
    cafe: [
        'bakery',
        'teahouse',

        // cross-group exceptions
        'restaurant',
        'dessert',
        'fast_food',
    ],

    fast_food: [
        'hot_dog',
        'sandwich',
        'falafel',
        'fried',

        // cross-group exceptions
        'dessert',
        'restaurant',
        'cafe',
    ],

    restaurant: [
        'seafood',
        'dizi',
        'ash',
        'kababi',
        'kalle_pache',
        'liver',

        // cross-group exceptions
        'cafe',
        'dessert',
    ],

    dessert: [
        'ice_cream',
        'juicery',
        'gelato',

        // cross-group exceptions
        'fast_food',
        'restaurant',
        'cafe',
    ],
} as const

// export const EATERY_EVENTS = ['none', 'halloween'] as const
// type Y = (typeof EATERY_EVENTS)[number]
// export const __ETAE: Y extends EateryEvent
//     ? EateryEvent extends Y
//         ? true
//         : 0
//     : 0 = true

export const EATERY_TAGS_DEFAULT: EateryTagsInfo = {
    has_self_service: 'idk',
    has_vegetarian_food: 'idk',
    is_suitable_for_children: 'idk',
    has_private_dining: 'idk',
    has_live_music: 'idk',
    is_suitable_for_large_groups: 'idk',
    is_suitable_for_pets: 'idk',
    has_dinner: 'idk',
    has_tv: 'idk',
    has_wifi: 'idk',
    has_outdoor_seating: 'idk',
    has_breakfast: 'idk',
    has_indoor_seating: 'idk',
    has_lunch: 'idk',
    has_parking: 'idk',
    has_solo_dining: 'idk',
    has_reservations: 'idk',
    has_takeaway: 'idk',
    has_toilet: 'idk',
    is_wheelchair_accessible: 'idk',
    has_dine_in: 'idk',
    has_delivery: 'idk',
    has_halal_food: 'idk',
    is_tourist_friendly: 'idk',
    has_drive_through: 'idk',
    has_vegan_food: 'idk',
    can_smoke_indoor: 'idk',
    allows_long_stays: 'idk',
    has_dj: 'idk',
    has_good_view: 'idk',
    has_qelyan: 'idk',
    is_photogenic: 'idk',
    is_suitable_for_reading: 'idk',
    is_suitable_for_social_working: 'idk',
}

export const EATERY_DISCOUNT: EateryDiscount = {
    available: 0,
    expires_at: 0,
    flags: {
        is_active: false,
    },
    max_amount: 0,
    min_order: 0,
    percentage: 0,
    rob_price: 0,
    starts_at: 0,
}

export const EATERY_FLAGS_DEFAULT: EateryFlagsInfo = {
    is_unique: false,
    is_brand: false,
    has_content_in_1_year: false,
    has_content_in_3_months: false,
    has_content_in_1_month: false,
    holds_birthday_parties: false,
    is_low_quality: false,
    is_close: false,
    is_for_guest: false,
    is_hidden: false,
    is_24_7: false,
    is_tourist_attraction: false,
    is_tender: false,
    trailer_m3u8_rendered: false,
    is_address_custom: false,
    is_address_google: false,
    is_address_parsimap: false,
    is_famous: false,
    has_employee: false,
    trailer_preview_rendered: false,
    is_perma_close: false,
}

export const EATERY_PRICE_RANGE_DEFAULT: PriceRange = {
    min: 0,
    max: 0,
    avg: 0,
}

export const TOP_IMPRESSIONS_DEFAULT: [
    EateryImpression,
    EateryImpression,
    EateryImpression,
    EateryImpression,
] = [
    { count: 0, impression: 0 },
    { count: 0, impression: 0 },
    { count: 0, impression: 0 },
    { count: 0, impression: 0 },
]

export const EATERY_INFO_DEFAULT: EateryInfo = {
    audience: 'none',
    decor: 'none',
    domain: 'none',
    group: 'unknown',
    warehouse: null,
    last_activity: 0,
    verified_until: 0,
    alias: '',
    impressions: '',
    top_impressions: TOP_IMPRESSIONS_DEFAULT,
    review_count: 0,
    stars: 0,
    gender_specific: 'none',
    trailer: '',
    address: '',
    updated_at: 0,
    compact_gene: null,
    gene: '',
    location: {
        latitude: 0,
        longitude: 0,
        altitude: 0,
        level: 0,
        sector: SECTOR_DEF,
    },
    menu: null,
    review: null,
    photos: ['', '', '', '', '', '', ''],
    entity_flags: ENTITY_FLAGS_DEFAULT,
    tags: EATERY_TAGS_DEFAULT,
    flags: EATERY_FLAGS_DEFAULT,
    tables: 0,
    zoom: 0,
    calc_zoom: 0,
    telephone: {
        phone: '',
        cc: 0,
    },
    stars_rate: {
        food: [0, 0, 0, 0, 0],
        service: [0, 0, 0, 0, 0],
        vibe: [0, 0, 0, 0, 0],
    },
    opening_hours: '',
    name: '',
    priority: 0,
    quality_score: 0,
    score: 0,
    best_dish: BEST_DISH_DEFAULT,
    cabbage: null,
    cabbage_stars: {
        food: [0, 0, 0, 0, 0],
        service: [0, 0, 0, 0, 0],
        vibe: [0, 0, 0, 0, 0],
    },
    popular_times: '',
    dish_count: 0,
    event: 'none',
    price_range: EATERY_PRICE_RANGE_DEFAULT,
}

export const DISH_KIND_SEARCH_ORDER = [
    'unknown',
    'appetizer',
    'food',
    'pizza',
    'burger',
    'steak',
    'chicken',
    'meat',
    'pasta',
    'seafood',
    '___fish',
    'sushi',
    'fried',
    'sandwich_hotdog',
    'sandwich',
    'kabab',
    'polo',
    'soup',
    'ash',
    'omelette',
    'friedegg',
    'noodle',
    'icecream',
    'croissant',
    'waffle',
    'cake',
    'salad',
    'cookie',
    'dessert',
    'bread',
    'tea',
    'coffee',
    'tea_herbal',
    'coffee_ice',
    'tea_ice',
    'milk',
    'sharbat',
    'juice',
    'chocolate',
    'milkshake',
    'smoothie',
    'soda',
    'drink',
    'other',
    'sides',
    'qelyan',
    'cigar',
    'service',
] as const

type __DKAST = (typeof DISH_KIND_SEARCH_ORDER)[number]
export const __DKAS: __DKAST extends DishKind
    ? DishKind extends __DKAST
        ? true
        : 0
    : 0 = true

export const FOOD_INDEX = DISH_KIND_SEARCH_ORDER.findIndex(s => s == 'food')
export const DRINK_INDEX = DISH_KIND_SEARCH_ORDER.findIndex(s => s == 'tea')
export const OTHER_INDEX = DISH_KIND_SEARCH_ORDER.findIndex(s => s == 'other')

export const FOODS = DISH_KIND_SEARCH_ORDER.slice(1, DRINK_INDEX).filter(
    s => !s.includes('___')
)
export const DRINKS = DISH_KIND_SEARCH_ORDER.slice(
    DRINK_INDEX,
    OTHER_INDEX
).filter(s => !s.includes('___'))

export const genericDrink: DishKind[] = ['drink']
export const hotDrinks: DishKind[] = ['coffee', 'tea', 'tea_herbal']
export const sodaDrinks: DishKind[] = ['soda']
export const coldDrinks: DishKind[] = ['coffee_ice', 'juice', 'sharbat', 'soda']
export const blendedDrinks: DishKind[] = ['milkshake', 'smoothie', 'chocolate']

export const pastries: DishKind[] = ['croissant', 'waffle', 'bread']

export const desserts: DishKind[] = ['dessert', 'icecream', 'cake', 'cookie']

export const fastFood: DishKind[] = [
    'pizza',
    'burger',
    'sandwich',
    'sandwich_hotdog',
    'fried',
]

export const seafoodDishes: DishKind[] = ['seafood', 'sushi']

export const soups: DishKind[] = ['soup', 'ash']

export const proteins: DishKind[] = ['kabab', 'chicken', 'steak', 'meat']

export const eggs: DishKind[] = ['omelette', 'friedegg'] as const

export const grains: DishKind[] = ['pasta', 'noodle', 'polo']

export const salads: DishKind[] = ['salad']
export const sides: DishKind[] = ['sides']

export const mains: DishKind[] = ['food']

const hookah = ['qelyan'] as const

export const others: DishKind[] = ['unknown', 'other']

export const DISH_FORM = ['unknown', 'food', 'drink']

export const STARS_TO_LABEL: Record<number, string> = {
    0: 'افتضاح',
    1: 'بد',
    2: 'متوسط',
    3: 'خوب',
    4: 'عالی!',
}

export const DISH_FLAGS_DEFAULT: DishFlags = {
    is_hidden: false,
}

export const DISH_TAGS_DEFAULT: DishTags = {
    is_breakfast: 'idk',
    is_dinner: 'idk',
    is_free: 'idk',
    is_available: 'idk',
    is_lunch: 'idk',
    _is_offering: 'idk',
    _is_spot_rate_price: 'idk',
}

export const DISH_DAILY_DEFAULT: DishDaily = {
    in_friday: false,
    is_daily: false,
    in_thursday: false,
    in_saturday: false,
    in_monday: false,
    in_tuesday: false,
    in_sunday: false,
    in_wednesday: false,
}

export const DAYS_OF_WEEK = [
    'saturday',
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
] as const

export const globalPriority: DishKind[] = [
    'food',
    'polo',
    'pizza',
    'burger',
    'chicken',
    'steak',
    'pasta',
    'sushi',
    'seafood',
    'kabab',
    'sandwich',
    'sandwich_hotdog',
    'fried',
    'ash',
    'soup',
    'salad',
    'sides',
    'drink',
] as const

export const cafePr: DishKind[] = [
    ...hotDrinks,
    ...coldDrinks,
    ...genericDrink,
    ...pastries,
    ...blendedDrinks,
    ...mains,
    ...fastFood,
    ...eggs,
    ...desserts,
    ...sodaDrinks,
    ...hookah,
]
export const restaurantPr: DishKind[] = [
    ...mains,
    ...fastFood,
    ...proteins,
    ...seafoodDishes,
    ...grains,

    ...soups,
    ...salads,
    ...eggs,

    ...desserts,
    ...pastries,

    ...genericDrink,
    ...hotDrinks,
    ...coldDrinks,
    ...blendedDrinks,
    ...sodaDrinks,

    ...sides,

    ...hookah,
] as const

export const seafoodPr: DishKind[] = [
    ...seafoodDishes,
    ...mains,

    ...fastFood,
    ...proteins,

    ...soups,

    ...grains,

    ...salads,

    ...desserts,
    ...pastries,

    ...genericDrink,
    ...coldDrinks,
    ...sodaDrinks,

    ...sides,
] as const

export const fastfoodPr: DishKind[] = [
    ...mains,
    ...fastFood,
    ...proteins,

    ...soups,
    ...salads,

    ...grains,

    ...desserts,
    ...pastries,

    ...genericDrink,
    ...coldDrinks,
    ...sodaDrinks,

    ...sides,
] as const

export const icecreamPr: DishKind[] = [
    ...desserts, // including 'icecream'
    ...pastries,

    ...genericDrink,
    ...coldDrinks,
    ...blendedDrinks,
    ...hotDrinks,

    ...sides,
] as const

export const pastryP: DishKind[] = [
    ...pastries,
    ...desserts,
    ...eggs,
    ...hotDrinks,
    ...coldDrinks,
    ...blendedDrinks,
    ...genericDrink,

    ...sides,
]

export function eateriesPriority(
    group: EateryGroup,
    domain: EateryDomain
): DishKind[] {
    if (domain !== 'none') {
        switch (domain) {
            case 'cafe':
                return cafePr

            case 'teahouse':
                return cafePr

            case 'bakery':
                return pastryP

            case 'restaurant':
                return restaurantPr

            case 'seafood':
                return seafoodPr

            case 'ice_cream':
                return icecreamPr

            case 'fast_food':
            case 'hot_dog':
            case 'sandwich':
            case 'falafel':
            case 'fried':
                return fastfoodPr

            case 'ash':
            case 'dizi':
            case 'kababi':
            case 'kalle_pache':
            case 'liver':
                return globalPriority

            case 'dessert':
            case 'juicery':
            case 'gelato':
                return icecreamPr
        }
    }

    switch (group) {
        case 'cafe':
            return cafePr

        case 'restaurant':
            return restaurantPr

        case 'fast_food':
            return fastfoodPr

        case 'dessert':
            return icecreamPr

        case 'unknown':
        default:
            return globalPriority
    }

    return globalPriority
}

export const SORT_ORDER = ['desc', 'asc'] as const

export const EATERY_SORT = ['review_count', 'star_sum', 'stars'] as const

export const FILTER_NUMBER_OPT = [
    'gt',
    'lt',
    'eq',
    'ne',
    'in',
    'not_in',
] as const

export const DISH_INFO_DEFAULT: DishInfo = {
    combo: 'unknown',
    growth: 0,
    gene: '',
    pond: '',
    eatery: '',
    price: 0,
    photos: ['', '', '', ''],
    kind: 'unknown',
    entity_flags: { ...ENTITY_FLAGS_DEFAULT, is_alive: true },
    name: '',
    note: '',
    flags: DISH_FLAGS_DEFAULT,
    tags: DISH_TAGS_DEFAULT,
    form: 'unknown',
    daily: DISH_DAILY_DEFAULT,
}

export const USER_CARD_DEF: UserCard = {
    gene: '',
    name: '',
    online_at: 0,
    photo: '',
    entity_flags: { is_alive: false },
    flags: {
        has_password: false,
        is_invited: false,
        is_banned: false,
        is_jailed: false,
    },
    growth: 0,
    username: '',
}

export const EATERY_CARD_DEF: EateryCard = {
    compact_gene: '',
    dish_count: 0,
    gene: '',
    location: {
        latitude: 0,
        longitude: 0,
        altitude: 0,
        level: 0,
        sector: SECTOR_DEF,
    },
    name: '',
    photo: '',
    review_count: 0,
    stars: 0,
    tables: 0,
    verified_until: 0,
    address: '',
    audience: 'none',
    decor: 'none',
    domain: 'none',
    group: 'unknown',
}

export const COUNT_DEFAULT: Count = { alive: 0, total: 0, max_page: 0, size: 0 }

export const TOPIC_INFO_DEF: TopicInfo = {
    gene: '',
    growth: 0,
    head: '',
    members: [],
    message_count: 0,
    message_sync: 0,
    pinned_messages: [],
    pond_count: 0,
    storage: 0,
    tail: '',
    title: '',
    owner: null,
}

export const TOPIC_DEF: Topic = {
    gene: '',
    growth: 0,
    head: '',
    members: [],
    message_count: 0,
    message_sync: 0,
    pinned_messages: [],
    pond_count: 0,
    storage: 0,
    tail: '',
    created_at: 0,
    entity_flags: { is_alive: false },
    owner: '',
    title: '',
}

export const FEEDBACK_DEF: FeedbackInfo = {
    detail: '',
    gene: '',
    kind: 'unknown',
    next: '',
    past: '',
    record: '',
    root: '',
    status: 'unknown',
    timestamp: 0,
    topic: '',
    user: '',
}

export const DECORS_PRIORITY_DEF: EateryDecor[] = [
    'luxury',
    'nature',
    'fantasy',
    'game',
    'musical',
    'traditional',
    'street',
    'bookish',
    'cultural',
    'minimal',
    'modern',
]
export const AUDIENCES_PRIORITY_DEF: EateryAudience[] = [
    'couples',
    'friends',
    'family',
    'business',
    'none',
]
export const EATERY_GROUP_PRIORITY_DEF: EateryGroup[] = [
    'cafe',
    'restaurant',
    'fast_food',
    'dessert',
]
export const EATERY_DOMAINS_PRIORITY_DEF: EateryDomain[] = [
    'cafe',
    'bakery',
    'teahouse',

    'restaurant',
    'seafood',
    'dizi',
    'ash',
    'kababi',
    'kalle_pache',
    'liver',

    'fast_food',
    'hot_dog',
    'sandwich',
    'falafel',
    'fried',

    'dessert',
    'ice_cream',
    'juicery',
    'gelato',
]
